import { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Entity } from '@omen-quest/grid-map';

// ============================================================================
// CONSTANTS
// ============================================================================

const ENTITY_TYPE_COLORS: Record<Entity['type'], string> = {
  player: 'text-amber-400',
  npc: 'text-blue-400',
  monster: 'text-red-400',
  object: 'text-neutral-400',
};

const ENTITY_TYPE_BADGE_COLORS: Record<Entity['type'], string> = {
  player: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  npc: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  monster: 'bg-red-500/20 text-red-400 border-red-500/30',
  object: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30',
};

const ENTITY_TYPE_LABELS: Record<Entity['type'], string> = {
  player: 'Player',
  npc: 'NPC',
  monster: 'Monster',
  object: 'Object',
};

const OBJECT_DESCRIPTIONS: Record<string, string> = {
  Fountain: 'A stone fountain, water trickling softly.',
  Chest: 'A sturdy chest, perhaps holding treasure.',
  Barrel: 'A wooden barrel, contents unknown.',
  Crate: 'A heavy crate, nailed shut.',
  Altar: 'A stone altar, worn by time.',
  Statue: 'A weathered statue gazing into the distance.',
  Well: 'A deep well — you hear water far below.',
  Anvil: 'A blacksmith\'s anvil, scarred by hammer blows.',
  Table: 'A simple wooden table.',
  Bookshelf: 'A tall bookshelf, crammed with dusty tomes.',
};

// ============================================================================
// HELPERS
// ============================================================================

export function getEntityDescription(entity: Entity): string {
  if (entity.metadata?.description) return entity.metadata.description as string;
  switch (entity.type) {
    case 'player': return 'You. The brave adventurer.';
    case 'npc': return 'A person you might speak with.';
    case 'monster': return 'A hostile creature lurking in the shadows.';
    case 'object': return OBJECT_DESCRIPTIONS[entity.name] ?? `A ${entity.name.toLowerCase()}.`;
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface EntityInspectCardProps {
  entity: Entity;
  onDismiss: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function EntityInspectCard({ entity, onDismiss }: EntityInspectCardProps) {
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  const threatLevel = entity.metadata?.threatLevel as string | undefined;

  return (
    <div
      className="absolute inset-0 z-30 flex items-end justify-center pb-8"
      onClick={onDismiss}
    >
      <div
        className="max-w-sm w-full mx-4 rounded-lg border border-neutral-700 bg-neutral-900/95 shadow-2xl shadow-black/60"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-4 py-3">
          {/* Header: name + badge + close */}
          <div className="flex items-center gap-2">
            <span className={cn('font-medium', ENTITY_TYPE_COLORS[entity.type])}>
              {entity.name}
            </span>
            <span className={cn(
              'text-[10px] uppercase tracking-wide font-medium rounded px-1.5 py-0.5 border',
              ENTITY_TYPE_BADGE_COLORS[entity.type],
            )}>
              {ENTITY_TYPE_LABELS[entity.type]}
            </span>
            <div className="flex-1" />
            <button
              onClick={onDismiss}
              className="text-neutral-500 hover:text-white transition-colors p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Threat level */}
          {threatLevel && (
            <div className="mt-1.5 text-xs text-red-400/80 font-medium uppercase tracking-wide">
              Threat: {threatLevel}
            </div>
          )}

          {/* Description */}
          <p className="mt-2 text-sm leading-relaxed text-neutral-300">
            {getEntityDescription(entity)}
          </p>
        </div>
      </div>
    </div>
  );
}
